import React, {useEffect, useState} from 'react'
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
} from 'antd'
import {dateFormat, DateString, ToDo} from '../../entities/todoList'
import {useCurrentUser} from '../../hooks/useCurrentUser'
import {instance as toDoService} from '../../services/ToDoService'
import {instance as journalService} from '../../services/JournalService'
import {PostgrestError} from '@supabase/supabase-js'
import dayjs, {Dayjs} from 'dayjs'
import {useIntl} from 'react-intl'
import {PlantFormDropdown, plantFormPlantToOption} from '../plantForm/components/PlantFormDropdown'
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import {
  ToDoTypeMetadata,
  PredefinedToDoTypeId,
  PredefinedToDoTypes,
  ToDoType,
  getToDoTypeInfo,
  EventTypeMetadata,
  CustomToDoType,
  CustomType,
  DefaultEventType,
  CustomJournalEventType,
  ToDoTypeOption,
} from '../../entities/journalRecord'
import {Link, useRevalidator, useSubmit} from 'react-router-dom'
import {CheckableListFormItem} from './CheckableList'
import {ToDoCustomTypeRadioButton} from './ToDoCustomTypeRadioButton'
import {ToDoTypeInput} from './ToDoTypeInput'
import {ToDoTypeFormSelector} from './ToDoTypeFormSelector'
import {FinishToDoButton} from './FinishToDoButton'
import {PlantCollection, PlantCollectionWithPlantIds} from '../../entities/plant'
import {SelectCollectionsModal} from './SelectCollectionsModal'
import {markPlantForRevalidation} from '../../routes/plantRevalidator'
import {markToDoForRevalidation} from '../../routes/todoRevalidator'
import {EmEmoji} from '../emoji/EmEmoji'
import {DateRangePicker} from '../ui/dateRangePicker/DateRangePicker'

const validateMessages = {
  required: '${label} is required!',
}

export type ToDoFormProps = {
  currentToDo?: ToDo
  projectId?: number
  isFinished?: boolean
  currentPlant?: {id: number; name: string}
  plantedPlantsNamesWithId?: {id: number; name: string; gardenPlanVariantId?: string | null}[]
  toDoTypes: CustomToDoType[]
  //setToDoTypes: (types: CustomToDoType[]) => void
  userEventTypes: CustomJournalEventType[]
  closeModal?: () => void
  setLoading?: (isLoading: boolean) => void
  isFullForm?: boolean
  userPlantCollections: PlantCollection[]
  userPlantCollectionsWithPlantsIds: PlantCollectionWithPlantIds[]
}

export function ToDoForm({
  currentToDo,
  projectId,
  isFinished,
  currentPlant,
  plantedPlantsNamesWithId,
  toDoTypes,
  //setToDoTypes,
  userEventTypes,
  closeModal = () => {},
  setLoading = () => {},
  isFullForm,
  userPlantCollections,
  userPlantCollectionsWithPlantsIds,
}: ToDoFormProps) {
  const [form] = Form.useForm()
  const [isRange, setIsRange] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [newUserType, setNewUserType] = useState<ToDoTypeOption>({
    id: null,
    icon: '🧑‍🌾',
    name: '',
  })
  const [editableTypeId, setEditableTypeId] = useState<number | null>(null)
  const user = useCurrentUser()
  const [messageApi, contextHolder] = message.useMessage()
  const submit = useSubmit()

  const intl = useIntl()
  const revalidator = useRevalidator()

  const availablePlants = plantedPlantsNamesWithId?.filter((plant) => !plant.gardenPlanVariantId)

  const [isSelectCollectionsModalOpen, setIsSelectCollectionsModalOpen] = useState(false)
  const [chosenUserCollections, setChosenUserCollections] = useState<
    Partial<Record<number, boolean>>
  >({})

  useEffect(() => {
    const newFieldValues = {importance: 'normal', ...(currentToDo || {})} as any
    let startDate = null as Dayjs | null
    let endDate = null as Dayjs | null
    if (currentToDo?.targetStartDateUtc) {
      startDate = dayjs(currentToDo.targetStartDateUtc, dateFormat)
    }
    if (currentToDo?.targetEndDateUtc) {
      endDate = dayjs(currentToDo.targetEndDateUtc, dateFormat)
    }
    // read once so the union narrows; a predefined type has no id of its own
    const currentType = currentToDo?.type
    const chosenType: ToDoTypeOption | null = !currentType
      ? null
      : currentType.type === 'custom'
      ? currentType.customToDoType
      : {id: null, name: currentType.type, icon: getToDoTypeInfo(currentType).icon}

    form.setFieldsValue({
      importance: 'normal',
      todoPlants:
        currentPlant && !currentToDo
          ? [plantFormPlantToOption(currentPlant, true)]
          : plantedPlantsNamesWithId
              ?.filter((plant) =>
                currentToDo?.plants?.map((todoPlant) => todoPlant.id === plant.id).some((x) => x)
              )
              .map((plant) =>
                plantFormPlantToOption(plant, currentPlant && plant.id === currentPlant.id)
              ) || null,
      ...(currentToDo || {}),
      date: startDate,
      dateRange: [startDate, endDate],
      chosenType,
    })
    setIsRange(Boolean(currentToDo?.targetEndDateUtc))
  }, [currentToDo])

  const onDeleteUserToDoType = async (deletedTypeId: number) => {
    const deletingResult = await toDoService.deleteUserToDoType(deletedTypeId)
    if (deletingResult.success) {
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
      // if (!isFullForm && form.getFieldValue('chosenType').id === deletedTypeId) {
      //   form.setFieldValue('chosenType', {id: null, icon: '📄', name: undefined})
      // }
      setEditableTypeId(null)
    }
  }

  const onEditUserToDoType = async (editedType: CustomType) => {
    const editingResult = await toDoService.editUserToDoType(editedType)
    if (editingResult.success) {
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
      // if (!isFullForm && form.getFieldValue('chosenType').id === editedType.id) {
      //   form.setFieldValue('chosenType', editedType)
      // }
    }
    return editingResult
  }

  const onCreateNewUserToDoType = async (name: string, icon: string) => {
    const result = await toDoService.createUserToDoType(name, icon)
    if (result.success) {
      setNewUserType({id: null, icon: '🧑‍🌾', name: ''})
      // setToDoTypes([...toDoTypes, {type: 'custom', customToDoType: result.data}])
      // form.setFieldsValue({chosenType: result.data})
      markPlantForRevalidation()
      markToDoForRevalidation()
      revalidator.revalidate()
    }
    return result
  }

  return (
    <div className={`todo-form ${isFullForm ? 'todo-form--full' : 'todo-form--modal'}`}>
      {contextHolder}
      <Spin spinning={isSubmitting}>
        <Form
          form={form}
          autoComplete="off"
          labelCol={isFullForm ? {span: 10} : {span: 10}}
          wrapperCol={{span: 14}}
          labelWrap
          layout={isFullForm ? 'vertical' : 'horizontal'}
          validateMessages={validateMessages}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
            }
          }}
          onFinish={async (formState) => {
            setSubmitting(true)
            let result: {success: boolean; error?: PostgrestError}
            const toDo: Omit<ToDo, 'id'> = {
              plants: isFullForm
                ? Object.keys(formState.plants || {})
                    .map(Number)
                    .filter((id) => !Number.isNaN(id) && formState.plants[id])
                    .map((id) => ({id}))
                : formState.todoPlants?.length > 0
                ? // the dropdown carries the planted plant id as its option value
                  formState.todoPlants.map((todoPlant: {value: string}) => ({
                    id: Number(todoPlant.value),
                  }))
                : [], //[{id: formState.todoPlants}],
              type: formState.chosenType?.id
                ? {type: 'custom', customToDoType: formState.chosenType}
                : {type: formState.chosenType?.name},
              comment: formState.comment,
              targetStartDateUtc:
                (isRange
                  ? formState.dateRange?.[0]?.format(dateFormat)
                  : formState.date?.format(dateFormat)) || null,
              targetEndDateUtc:
                (isRange ? formState.dateRange?.[1]?.format(dateFormat) : null) || null,
              importance: formState.importance,
              creationDateUtc: dayjs().format(dateFormat) as DateString,
              ownerId: user.id,
            }
            if (currentToDo) {
              result = await toDoService.editToDo({
                ...currentToDo,
                ...toDo,
              })
            } else {
              setLoading(true)
              result = await toDoService.upsertToDo(toDo)
            }

            if (result.success) {
              setLoading(false)
              form.resetFields()
              setIsRange(false)
              closeModal()
              submit(currentPlant ? {id: String(currentPlant.id)} : null, {method: 'post'})
            } else {
              setLoading(false)
              messageApi.open({
                type: 'error',
                content: result.error?.message,
              })
            }
            setSubmitting(false)
          }}
        >
          {isFullForm && (
            <div className="todo-form__type column">
              <Form.Item
                name="chosenType"
                label={intl.formatMessage({id: 'taskType'})}
                className="todo-type-form-item"
              >
                <Radio.Group>
                  <div className="todo-type-selector">
                    {PredefinedToDoTypes.map((item) => (
                      <Radio key={item} value={ToDoTypeMetadata[item]}>
                        <EmEmoji id={ToDoTypeMetadata[item].icon} size="1em"></EmEmoji>

                        {intl.formatMessage({id: `eventType-${item}`})}
                      </Radio>
                    ))}
                    {/* a type the user deleted has no definition left to offer */}
                    {toDoTypes?.map(({customToDoType}) =>
                      customToDoType ? (
                        <ToDoCustomTypeRadioButton
                          key={customToDoType.id}
                          customToDoType={customToDoType}
                          editableTypeId={editableTypeId}
                          setEditableTypeId={setEditableTypeId}
                          onChangeUserType={onEditUserToDoType}
                          onDeleteUserType={onDeleteUserToDoType}
                        />
                      ) : null
                    )}
                  </div>
                </Radio.Group>
              </Form.Item>

              <div className="new-type-input">
                <ToDoTypeInput userType={newUserType} setUserType={setNewUserType} />
                <Button
                  type="primary"
                  disabled={newUserType.name === ''}
                  onClick={(e) => onCreateNewUserToDoType(newUserType.name, newUserType.icon)}
                >
                  <PlusOutlined />
                </Button>
              </div>
            </div>
          )}
          {!isFullForm && (
            <>
              <Form.Item
                name="chosenType"
                valuePropName="chosenType"
                label={intl.formatMessage({id: 'taskType'})}
              >
                <ToDoTypeFormSelector
                  commonTypes={(Object.keys(ToDoTypeMetadata) as PredefinedToDoTypeId[]).map(
                    (type) => ({
                      id: null,
                      name: type,
                      icon: ToDoTypeMetadata[type].icon,
                    })
                  )}
                  userTypes={toDoTypes.flatMap(({customToDoType}) =>
                    customToDoType ? [customToDoType] : []
                  )}
                  createNewItem={onCreateNewUserToDoType}
                  editType={
                    onEditUserToDoType
                    //   async (editedType: CustomType) => {
                    //   const editingResult = await toDoService.editUserToDoType(editedType)
                    //   if (editingResult.success) {
                    //     setToDoTypes(
                    //       toDoTypes.map((type) => {
                    //         if (type.customToDoType.id === editedType.id) {
                    //           return {type: 'custom', customToDoType: editedType}
                    //         } else {
                    //           return type
                    //         }
                    //       })
                    //     )
                    //   }
                    //   return editingResult
                    // }
                  }
                  deleteUserType={onDeleteUserToDoType}
                />
              </Form.Item>
            </>
          )}

          {isFullForm ? (
            <div className="todo-form__plant column">
              <Form.Item name="plants" label={intl.formatMessage({id: 'plants'})}>
                <CheckableListFormItem
                  elements={availablePlants}
                  currentPlant={currentPlant}
                />
              </Form.Item>
              <Button onClick={() => setIsSelectCollectionsModalOpen(true)}>
                {intl.formatMessage({id: 'collections'})}
              </Button>
            </div>
          ) : (
            <PlantFormDropdown currentPlant={currentPlant} plantsList={availablePlants} />
          )}

          <div className="todo-form__fields column">
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <Form.Item label={intl.formatMessage({id: 'dateRange'})}>
                <Switch
                  checked={isRange}
                  onChange={setIsRange}
                  aria-label={intl.formatMessage({id: 'dateRange'})}
                />
              </Form.Item>
              {isRange ? (
                <Form.Item name="dateRange" label={intl.formatMessage({id: 'dateRange'})}>
                  <DateRangePicker />
                </Form.Item>
              ) : (
                <Form.Item name="date" label={intl.formatMessage({id: 'date'})}>
                  <DatePicker />
                </Form.Item>
              )}
            </div>
            <Form.Item name="comment" label={intl.formatMessage({id: 'comment'})}>
              <Input.TextArea />
            </Form.Item>

            <Form.Item
              label={intl.formatMessage({id: 'importance'})}
              name="importance"
              className="todo-form__importance"
            >
              <Radio.Group>
                <Radio.Button value="critical">{intl.formatMessage({id: 'critical'})}</Radio.Button>
                <Radio.Button value="urgent">{intl.formatMessage({id: 'urgent'})}</Radio.Button>
                <Radio.Button value="normal">{intl.formatMessage({id: 'normal'})}</Radio.Button>
                <Radio.Button value="low">{intl.formatMessage({id: 'low'})}</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>

          {/* Laid out as a flex row rather than floated: floats are out of flow,
              so on a form too narrow to hold every button the ones that used to
              float right ended up underneath the submit button instead of
              wrapping onto a line of their own. */}
          <div className="todo-form__actions">
            <Form.Item className="todo-form__submit column">
              <Button htmlType="submit" type="primary">
                {intl.formatMessage({id: 'save'})}
              </Button>
            </Form.Item>

            <div className="todo-form__actions__secondary">
              {!isFullForm && (
                <Button
                  type="link"
                  onClick={() => {
                    closeModal()
                  }}
                >
                  {intl.formatMessage({id: 'cancel'})}
                </Button>
              )}

              {currentToDo && (
                <Popconfirm
                  placement="bottom"
                  title={intl.formatMessage({id: 'areYouSureYouWantToDelete'})}
                  description={intl.formatMessage({id: 'thisOperationIsIrreversible'})}
                  icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                  onConfirm={async () => {
                    const result = await toDoService.deleteToDo(currentToDo.id)

                    if (result.success) {
                      closeModal()
                      submit(currentPlant ? {id: String(currentPlant.id)} : null, {method: 'post'})
                    } else {
                      messageApi.open({
                        type: 'error',
                        content: result.error?.message,
                      })
                    }
                  }}
                  okText={intl.formatMessage({id: 'yes'})}
                  cancelText={intl.formatMessage({id: 'no'})}
                >
                  <Button type="link">{intl.formatMessage({id: 'delete'})}</Button>
                </Popconfirm>
              )}

              {currentToDo && (
                <FinishToDoButton
                  userJournalEventTypes={userEventTypes}
                  finishedTodo={currentToDo}
                  isText={true}
                />
              )}
            </div>
          </div>
        </Form>
      </Spin>
      <SelectCollectionsModal
        userCollections={userPlantCollections}
        chosenUserCollections={chosenUserCollections}
        setChosenUserCollections={setChosenUserCollections}
        isOpen={isSelectCollectionsModalOpen}
        onCancel={() => {
          setIsSelectCollectionsModalOpen(false)
          setChosenUserCollections({})
        }}
        onCloseNewSelected={() => {
          setIsSelectCollectionsModalOpen(false)

          const plantIds = userPlantCollectionsWithPlantsIds.flatMap((collection) =>
            chosenUserCollections[collection.id]
              ? collection.plantsIds.map((plant) => plant.plantedPlantsId)
              : []
          )
          const newSelectedPlants: Record<number, boolean> = form.getFieldValue('plants') || {}
          for (const plantId of plantIds) {
            if (availablePlants?.some((plant) => plant.id === plantId)) {
              newSelectedPlants[plantId] = true
            }
          }

          form.setFieldsValue({
            plants: newSelectedPlants,
          })
          setChosenUserCollections({})
        }}
      />
    </div>
  )
}
